// The application chrome: navigation, notices, alerts and confirmations (SPEC 8.3).
import { $, html, raw, escapeHtml, errorText } from "./dom";
import type { AppContext } from "../context";
import { signOut } from "../auth";

interface NavEntry { hash: string; label: string; icon: string; }

const NAV: NavEntry[] = [
  { hash: "#/", label: "Home", icon: "home" },
  { hash: "#/servers", label: "Servers", icon: "server" },
  { hash: "#/services", label: "Services", icon: "layers" },
  { hash: "#/users", label: "User → Org", icon: "users" },
  { hash: "#/audit", label: "Audit log", icon: "list-check" },
  { hash: "#/setup", label: "Setup", icon: "gear" }
];

/** Builds the chrome once and returns the element pages render into. */
export function mountShell(root: HTMLElement, ctx: AppContext): HTMLElement {
  root.innerHTML = html`
    <calcite-navigation slot="header">
      <calcite-navigation-logo slot="logo" heading="Org Filter Manager" description="${ctx.me.portalName}"></calcite-navigation-logo>
      <calcite-menu slot="content-end">
        ${raw(NAV.map((n) => html`<calcite-menu-item text="${n.label}" icon-start="${n.icon}" href="${n.hash}" data-nav="${n.hash}"></calcite-menu-item>`).join(""))}
      </calcite-menu>
      <calcite-menu slot="user">
        <calcite-menu-item text="${ctx.me.fullName}" icon-start="user" id="sign-out" text-enabled></calcite-menu-item>
      </calcite-menu>
    </calcite-navigation>
    <div id="content"></div>
    <calcite-alert id="app-alert" slot="alerts" auto-close auto-close-duration="medium" placement="bottom-end" kind="success">
      <div slot="title" id="app-alert-title"></div>
      <div slot="message" id="app-alert-message"></div>
    </calcite-alert>
  `;
  $(root, "#sign-out").addEventListener("click", () => signOut());
  return $(root, "#content");
}

export function setActiveNav(hash: string): void {
  const normalised = hash === "" || hash === "#" ? "#/" : hash;
  for (const item of document.querySelectorAll<HTMLElement & { active?: boolean }>("[data-nav]")) {
    item.active = item.dataset.nav === normalised;
  }
}

export type NoticeKind = "brand" | "info" | "success" | "warning" | "danger";

/** A Calcite notice as markup, for use inside `html` templates. */
export function notice(kind: NoticeKind, title: string, message: string, options: { closable?: boolean } = {}): string {
  return html`
    <calcite-notice open kind="${kind}" width="full" ${raw(options.closable ? "closable" : "")} style="margin-block-end: 0.75rem">
      <div slot="title">${title}</div>
      <div slot="message">${message}</div>
    </calcite-notice>
  `;
}

export function alertUser(kind: NoticeKind, title: string, message = ""): void {
  const alert = document.getElementById("app-alert") as (HTMLElement & { kind: string; open: boolean }) | null;
  if (!alert) return;
  alert.kind = kind;
  (document.getElementById("app-alert-title") as HTMLElement).textContent = title;
  (document.getElementById("app-alert-message") as HTMLElement).textContent = message;
  alert.open = true;
}

export function alertError(title: string, e: unknown): void {
  console.error(title, e);
  alertUser("danger", title, errorText(e));
}

/** Full-panel placeholder while a page loads its data. */
export function loading(message = "Loading…"): string {
  return html`<calcite-loader label="${message}" text="${message}"></calcite-loader>`;
}

export interface ConfirmOptions {
  title: string;
  message: string;
  /** Text the administrator must type verbatim; used for the dangerous choices (SPEC 6.6). */
  requireText?: string;
  confirmLabel?: string;
  kind?: NoticeKind;
}

/**
 * A modal confirmation. `requireText` is what makes "pass" and other unfiltered
 * choices deliberate rather than a mis-click.
 */
export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const dialog = document.createElement("calcite-dialog");
    dialog.setAttribute("modal", "");
    dialog.setAttribute("heading", options.title);
    dialog.setAttribute("kind", options.kind ?? "brand");
    dialog.setAttribute("width-scale", "s");
    dialog.innerHTML = html`
      <div style="display:grid; gap:0.75rem">
        <div>${raw(escapeHtml(options.message).replace(/\n/g, "<br>"))}</div>
        ${raw(options.requireText
          ? html`<calcite-label>Type <b>${options.requireText}</b> to confirm
              <calcite-input-text id="confirm-text" autocomplete="off"></calcite-input-text>
            </calcite-label>`
          : "")}
      </div>
      <calcite-button slot="footer-end" appearance="outline" kind="neutral" id="confirm-cancel">Cancel</calcite-button>
      <calcite-button slot="footer-end" kind="${options.kind === "danger" ? "danger" : "brand"}" id="confirm-ok" ${raw(options.requireText ? "disabled" : "")}>
        ${options.confirmLabel ?? "Confirm"}
      </calcite-button>
    `;
    document.body.appendChild(dialog);

    const ok = $(dialog, "#confirm-ok") as HTMLElement & { disabled: boolean };
    if (options.requireText) {
      const input = $(dialog, "#confirm-text") as HTMLElement & { value: string };
      input.addEventListener("calciteInputTextInput", () => {
        ok.disabled = input.value.trim() !== options.requireText;
      });
    }

    const close = (result: boolean) => {
      resolve(result);
      dialog.removeAttribute("open");
      setTimeout(() => dialog.remove(), 500);
    };
    $(dialog, "#confirm-cancel").addEventListener("click", () => close(false));
    ok.addEventListener("click", () => close(true));
    dialog.addEventListener("calciteDialogClose", () => resolve(false));
    // setTimeout, not requestAnimationFrame: a frame callback never fires while the tab is
    // backgrounded, so the dialog would be appended and silently never open.
    setTimeout(() => dialog.setAttribute("open", ""), 0);
  });
}

/** Covers an element while an action runs, so a slow Admin API call cannot be double-fired. */
export async function withBusy<T>(container: HTMLElement, message: string, fn: () => Promise<T>): Promise<T> {
  const scrim = document.createElement("calcite-scrim");
  scrim.setAttribute("loading", "");
  scrim.textContent = message;
  container.style.position = container.style.position || "relative";
  container.appendChild(scrim);
  try {
    return await fn();
  } finally {
    scrim.remove();
  }
}
